import React from 'react';
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

interface ExhibitAccessSettingsProps {
  showExhibitAccess: boolean;
  exhibitAccessButtons: {
    enterNumber: boolean;
    scanQR: boolean;
    favorites: boolean;
    browseTours: boolean;
  };
  saving: boolean;
  onUpdateSetting: (key: string, value: boolean | string | object) => Promise<void>;
  onUpdateButtonSetting: (buttonKey: string, value: boolean) => Promise<void>;
}

export function ExhibitAccessSettings({
  showExhibitAccess,
  exhibitAccessButtons,
  saving,
  onUpdateSetting,
  onUpdateButtonSetting
}: ExhibitAccessSettingsProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
        <div className="space-y-1">
          <Label htmlFor="show-exhibit-access" className="text-sm font-medium text-foreground">Show Access Options</Label>
          <p className="text-sm text-muted-foreground">
            Display the exhibit access options section
          </p>
        </div>
        <Switch
          id="show-exhibit-access"
          checked={showExhibitAccess}
          onCheckedChange={(value) => onUpdateSetting('showExhibitAccess', value)}
          disabled={saving}
        />
      </div>

      {showExhibitAccess && (
        <>
          <Separator className="bg-border/50" />
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-primary rounded-full"></div>
              <h4 className="text-sm font-semibold text-foreground">Individual Access Buttons</h4>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-background border border-border/40 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="enter-number" className="text-sm font-medium text-foreground">Enter Number</Label>
                  <p className="text-sm text-muted-foreground">
                    Access exhibits by entering their number
                  </p>
                </div>
                <Switch
                  id="enter-number"
                  checked={exhibitAccessButtons.enterNumber}
                  onCheckedChange={(value) => onUpdateButtonSetting('enterNumber', value)}
                  disabled={saving}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-background border border-border/40 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="scan-qr" className="text-sm font-medium text-foreground">Scan QR Code</Label>
                  <p className="text-sm text-muted-foreground">
                    Access exhibits by scanning QR codes
                  </p>
                </div>
                <Switch
                  id="scan-qr"
                  checked={exhibitAccessButtons.scanQR}
                  onCheckedChange={(value) => onUpdateButtonSetting('scanQR', value)}
                  disabled={saving}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-background border border-border/40 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="favorites" className="text-sm font-medium text-foreground">Favorites</Label>
                  <p className="text-sm text-muted-foreground">
                    View saved favorite exhibits
                  </p>
                </div>
                <Switch
                  id="favorites"
                  checked={exhibitAccessButtons.favorites}
                  onCheckedChange={(value) => onUpdateButtonSetting('favorites', value)}
                  disabled={saving}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-background border border-border/40 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="browse-tours" className="text-sm font-medium text-foreground">Browse Tours</Label>
                  <p className="text-sm text-muted-foreground">
                    Explore available guided tours
                  </p>
                </div>
                <Switch
                  id="browse-tours"
                  checked={exhibitAccessButtons.browseTours}
                  onCheckedChange={(value) => onUpdateButtonSetting('browseTours', value)}
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}